import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildSeoTitle, buildMetaDescription } from 'shared/lib/seoHelpers';
import apiClient from 'userportal/lib/ApiClient';
import Card, { CardList } from 'shared/components/Card';
import ContentModuleView from 'shared/components/ContentModule/View';
import { Submit } from 'shared/components/Form';
import NotFound from 'userportal/components/NotFound';
import JobPreview from 'userportal/components/Job/Preview';
import Page from 'userportal/components/Page';
import { scrollToId } from 'shared/lib/scrollTo';
import Subheader from 'shared/components/Subheader';
import Loadable from 'shared/components/Loadable';
import Noop from 'shared/components/Noop';
import { CONTENT_MODULE_TYPE } from 'shared/constants';
import { userportal as routes, createRoute, createUserportalLink, createStaticsLink } from 'shared/routes';
import { selectors as authSelectors } from 'userportal/ducks/auth';
import CompanyNoImageHeader from './NoImageHeader';
import CompanyWithImageHeader from './WithImageHeader';
import styles from './Company.module.scss';

const previewJobs = 5;

const CompanyPage = ({ slug: externalSlug, isHomepageView }) => {
  const { t, i18n } = useTranslation('userportal');
  const navigate = useNavigate();
  const location = useLocation();
  const { slug: paramSlug } = useParams();
  const slug = externalSlug || paramSlug;
  const loggedIn = useSelector(authSelectors.isLoggedIn);
  const [state, setState] = useState({
    company: null,
    loading: true,
    error: null,
    errorCode: null
  });

  const load = async () => {
    setState({ error: null, errorCode: null, loading: true });
    try {
      const company = await apiClient.get(`/company/${slug}/profile`);
      setState({ error: null, errorCode: null, loading: false, company });
    } catch (e) {
      setState({
        error: e.status !== 404 ? e.error : null,
        errorCode: e.status,
        loading: false
      });
    }
  };

  useEffect(() => {
    if (!state.company) {
      load();
    }
  }, []);

  useEffect(() => {
    if (state.company) {
      load();
    }
  }, [i18n.language]);

  useEffect(() => {
    if (state.company && state.company.redirection) {
      navigate(createRoute(routes.company.detail, { slug: state.company.slug }), { replace: true });
    }
  }, [state.company]);

  useEffect(() => {
    if (!state.loading && location.hash) {
      scrollToId(location.hash);
    }
  }, [state.loading, location.hash]);

  if ((location.search || '').length > 0 && !isHomepageView) {
    return (<Navigate to={createRoute(routes.company.detail, { slug })} replace />);
  }

  const handleAllJobsClick = () => navigate(createRoute(routes.root, {}, { company: slug }));

  const { loading, company, error, errorCode } = state;

  const companyDescriptionModule = company ? company.modules.find(({ type }) => type === CONTENT_MODULE_TYPE.TEXT) : null;
  let companyDescription = '';
  if (company) {
    if (company.description) {
      companyDescription = buildMetaDescription(company.description);
    } else if (companyDescriptionModule) {
      companyDescription = buildMetaDescription(companyDescriptionModule.content.text);
    } else if (company.jobs.total > 0) {
      companyDescription = `${company.name}. ${t('page.company.jobsOpenedTitle')}: `;
      companyDescription += company.jobs.hits.map(job => job.title).join(', ');
    } else {
      companyDescription = `${company.name}. ${t('common:seoDescription')}`;
    }
  }

  const Wrapper = isHomepageView ? Noop : Page;

  return (
    <Wrapper
      _initialRow={false}
      _noTopPadding={company && company.headerImage}
      initialContainer={false}
      helmetTitle={company ? buildSeoTitle(t, company.name) : ''}
      helmetDescription={companyDescription}
      seoUrl={company ? createUserportalLink(routes.company.detail, { slug: company.slug }) : null}
      ogImage={company ? createStaticsLink(`company-og/${company.ogImage}`) : null}
      subheader={errorCode === 404 && (
        <Subheader center title={t('page.company.notFound.title')}>
          <span>{t('page.company.notFound.subtitle')}</span>
        </Subheader>
      )}
    >
      <Loadable
        loading={loading}
        error={error}
        render={() => {
          if (errorCode === 404) {
            return (
              <NotFound
                lines={[t('page.company.notFound.p1'), t('page.company.notFound.p2')]}
                ctaTitle={t('page.company.notFound.cta')}
                ctaRoute="/"
              />
            );
          }

          const stats = company.jobs.hits.reduce((acc, curr) => {
            const cities = curr.locations.map(loc => loc.city ? loc.city : null).filter(Boolean);
            const countries = curr.locations.map(loc => !loc.city ? loc.country : null).filter(Boolean);
            const fields = curr.fields.map(field => field.slug);
            acc.cities = acc.cities.concat(cities);
            acc.countries = acc.countries.concat(countries);
            acc.fields = acc.fields.concat(fields);

            return acc;
          }, { cities: [], countries: [], fields: [] });

          stats.cities = _.uniq(stats.cities);
          stats.countries = _.uniq(stats.countries);
          stats.fields = _.uniq(stats.fields).map(f => t(`common:field.${f}`));

          return (
            <>
              {company.headerImage ? (
                <CompanyWithImageHeader company={company} />
              ) : (
                <CompanyNoImageHeader company={company} />
              )}
              <div className="container">
                {company.description && (
                  <p className={styles.description}>
                    {company.description}
                  </p>
                )}
              </div>
              {company.modules.map(({ id, type, content }) => (
                <div key={id} className={!content.fullWidth ? 'container' : null}>
                  <ContentModuleView type={type} content={content} />
                </div>
              ))}
              <div className="container">
                <h2 id="vacancies">{t('page.company.jobsOpenedTitle')}</h2>
                {company.jobs.total > 0 ? (
                  <div className={styles.jobList}>
                    <CardList>
                      {company.jobs.hits.slice(0, previewJobs).map(job => (
                        <Card key={job.slug} noBorder spacingBottom="small">
                          <JobPreview
                            job={job}
                            loggedIn={loggedIn}
                            noLogo
                            withSmallTags={false}
                          />
                        </Card>
                      ))}
                    </CardList>
                    {company.jobs.total > 0 && (
                      <div className={styles.showMore}>
                        <Submit
                          iconClassName="la la-search"
                          title={t('page.company.allJobsLink', { count: company.jobs.total })}
                          onClick={handleAllJobsClick}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <p>{t('page.company.noOpenedPositions')}</p>
                )}
                <h2>{t('page.company.infoTitle', { name: company.name })}</h2>
                <div className={cn('row', styles.infoRow)}>
                  <div className={cn('col-xl-4 col-lg-4 col-md-6 col-sm-6 col-xs-12', styles.item)}>
                    <i className="la la-map-marked-alt" />
                    <div className={styles.content}>
                      <h4>{t('page.company.address')}</h4>
                      <span>{company.street}, {company.zip} {company.city.title}</span>
                    </div>
                  </div>
                  {company.www && (
                    <div className={cn('col-xl-4 col-lg-4 col-md-6 col-sm-6 col-xs-12', styles.item)}>
                      <i className="la la-link" />
                      <div className={styles.content}>
                        <h4>{t('page.company.www')}</h4>
                        <span><a href={company.www} target="_blank" rel="noopener noreferrer">{company.www}</a></span>
                      </div>
                    </div>
                  )}
                  <div className={cn('col-xl-4 col-lg-4 col-md-6 col-sm-6 col-xs-12', styles.item)}>
                    <i className="la la-tags" />
                    <div className={styles.content}>
                      <h4>{t('page.company.openedPositionsCount')}</h4>
                      <span>{company.jobs.total}</span>
                    </div>
                  </div>
                  {(stats.cities.length > 0 || stats.countries.length > 0) && (
                    <div className={cn('col-xl-4 col-lg-4 col-md-6 col-sm-6 col-xs-12', styles.item)}>
                      <i className="la la-map-marked-alt" />
                      <div className={styles.content}>
                        <h4>{t('page.company.localities')}</h4>
                        <span>
                          {stats.cities.join(', ')}{stats.cities.length > 0 && stats.countries.length > 0 && ', '}{stats.countries.join(', ')}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className={cn('col-xl-4 col-lg-4 col-md-6 col-sm-6 col-xs-12', styles.item)}>
                    <i className={company.agency ? 'la la-user-tie' : 'la la-building'} />
                    <div className={styles.content}>
                      <h4>{t('common:form.companyType')}</h4>
                      <span>{company.agency ? t('common:companyType.agency') : t('common:companyType.employer')}</span>
                    </div>
                  </div>
                  {!company.agency && (
                    <div className={cn('col-xl-4 col-lg-4 col-md-6 col-sm-6 col-xs-12', styles.item)}>
                      <i className="la la-users" />
                      <div className={styles.content}>
                        <h4>{t('page.company.size')}</h4>
                        <span>{company.size ? t(`common:companySizeDetail.${company.size}`) : t('common:notSpecified')}</span>
                      </div>
                    </div>
                  )}
                  <div className={cn('col-xl-4 col-lg-4 col-md-6 col-sm-6 col-xs-12', styles.item)}>
                    <i className="la la-list-alt" />
                    <div className={styles.content}>
                      <h4>{t('page.company.fields')}</h4>
                      <span>{stats.fields.join(', ')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
        }}
      />
    </Wrapper>
  );
};

export default CompanyPage;
